import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button } from "../../../../../components/common/Button";
import { ReactComponent as NotifyIcon } from "../../../assets/notify-icon.svg";
import {
  getCNYQuotationCategory,
  KlashaCNYQuotation,
  uploadCNYMerchantFile,
} from "../../../redux/slice";
import {BalanceExceptionWrapper, ContentWrapper, Error, InputWrapper} from "../styles";
import CustomSelect from "../../../../../components/common/CustomSelect";
import Loader from "../../../../../components/common/Loader";
import InputAndSelect from "../../../../../components/common/InputAndSelect";
import { RateBox } from "../../../components/Withdrawal/styles";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {Link, useParams} from "react-router-dom";
import {
  cnyPaymentPurpose,
  cnyTypeOfpaymentMethod,
  MIN_CNY_AMOUNT,
  paymentMethodConfigurations,
} from "../../../const";
import { debounce } from "lodash";
import { NetworkErrorAlertContext } from "../../../../../context/NetworkErrorAlert";
import { savedCNYData } from "../../../redux/formDataSlice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../utils/encryptionAlgorithm";
import { merchantBusinessKeys } from "../../../../Settings/settingsSlice";
import { DocumentUpload } from "../../../components/DocumentUpload";
import { formatCurrencyAmount } from "../../../../../utils/currency";
import {capitalizeCurrencyCode} from "../../../../../utils/helpers";

type Props = {
  handleNext?: () => void;
  handlePrevious?: () => void;
};

type WalletProps = {
  currency: string;
};

type UserData = {
  amount: number;
  convertedAmount: number;
  sourceCurrency?: string;
  sourceAmount: number | string;
  convertedRate?: number | string;
  destinationCurrency?: string;
  paymentPurpose?: string;
  quotationId?: string;
  paymentMethod?: string;
  rate: any;
  category: string[];
  totalAmount: number;
  file: File;
  fee: number;
  expiration: string;
  reference: string;
  balanceError?: boolean;
};

const Amount = ({ handleNext }: Props) => {
  const CNYData = useAppSelector((state) => state.formData?.cnyData || {});
  const [userData, setUserData] = useState<UserData>(CNYData);
  const { keys } = useAppSelector((state) => state.settings);
  const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
  const { businessId } = useAppSelector((state) => state.users);
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );
  const { currencyCode } = useParams();
  const dispatch = useAppDispatch();

  const filteredWallets = wallets as WalletProps[];

  const CURRENT_WALLET = wallets?.find((wallet) => wallet.currency === "USD");

  const uniqueCurrencies = Array.from(
    new Set(filteredWallets?.map((wallet) => wallet.currency)),
  );

  const resetUserData = (data: UserData): UserData => {
    return Object.keys(data).reduce((acc, key) => {
      acc[key as keyof UserData as any] = null;
      return acc;
    }, {} as UserData);
  };

  const options = uniqueCurrencies.map((currency) => ({
    label: currency,
    value: currency,
  }));

    useEffect(() => {
        if (!CURRENT_WALLET) return;

        if(CURRENT_WALLET.availableBalance < userData.amount){
            handleDataUpdate("balanceError", true)
        }else{
            handleDataUpdate("balanceError", false)


        }
    }, [CURRENT_WALLET, userData.amount]);


  /*
   Remove USD and allow other currency options when business decides otherwise
   */
  const walletToDebit = options?.filter((option) => option.value === "USD");

  const handleAmountChange = (enteredAmount) => {
    setUserData((prevData) => ({
      ...prevData,
      amount: enteredAmount,
    }));
  };

  const handleDataUpdate = (field: keyof UserData, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    dispatch(savedCNYData(userData));
  }, [userData, dispatch]);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      const action = await dispatch(getCNYQuotationCategory());
      if (getCNYQuotationCategory.fulfilled.match(action)) {
        handleDataUpdate("category", action.payload);
      }
    };

    fetchData();
  }, [dispatch]);

  const debouncedGetKlashaQuotation = useCallback(
    debounce(async () => {
      if (
        !userData?.amount ||
        userData?.amount < MIN_CNY_AMOUNT ||
        !userData?.paymentMethod ||
        !userData?.paymentPurpose ||
        !businessId
      ) {
        return;
      }

      const config = paymentMethodConfigurations[userData.paymentMethod] || {
        serviceCode: "",
        service: "",
        fundSource: "",
      };

      const data = {
        sourceCurrency: "USD",
        destinationCurrency: "CNY",
        destinationAmount: userData.amount,
        ...config,
      };

      try {
        const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
          businessId,
          data,
        );
        const payload = { message: encryptedMessage };
        const token = publicKey || keys?.data?.publicKey;

        const action = await dispatch(
          KlashaCNYQuotation({ message: payload, token }),
        );

        if (KlashaCNYQuotation.fulfilled.match(action)) {
          const { fxRate, fee, sourceAmount, expiration, id, reference } =
            action.payload;

          const convertedValue = Number(userData?.amount / fxRate);

          setUserData((prev) => ({
            ...prev,
            rate: fxRate,
            convertedAmount: convertedValue,
            convertedRate: convertedValue,
            totalAmount: Number(sourceAmount + fee),
            fee,
            expiration,
            quotationId: id,
            reference,
            sourceAmount,
          }));
        }
      } catch (err) {
        // failed quotation
      }
    }, 1000),
    [
      dispatch,
      businessId,
      keys?.data?.publicKey,
      userData?.amount,
      userData?.paymentMethod,
      userData?.paymentPurpose,
    ],
  );

  const { amount, paymentMethod, paymentPurpose } = userData;

  useEffect(() => {
    if (
      amount >= MIN_CNY_AMOUNT &&
      paymentMethod &&
      paymentPurpose &&
      businessId
    ) {
      debouncedGetKlashaQuotation();
    }
  }, [amount, paymentMethod, paymentPurpose, businessId]);

  useEffect(() => {
    if (!amount || Number(amount) < MIN_CNY_AMOUNT) {
      setUserData((prev) => resetUserData(prev));
    }
  }, [amount]);

  const handleNextStep = () => {
    dispatch(savedCNYData(userData));
    handleNext();
  };


  const publicKey = keys?.data?.publicKey;

  const handleFileChange = async (title: string, file: File | null) => {
    if (!file) return;

    const action = await dispatch(
      uploadCNYMerchantFile({
        file,
        purpose: paymentPurpose ?? "invoice",
        attachmentType: "COMMERCIAL_DOCUMENTS",
        xAuthToken: publicKey,
      }),
    );

    if (uploadCNYMerchantFile.rejected.match(action)) {
      onShowErrorAlert("error", action.payload as string);
    } else if (uploadCNYMerchantFile.fulfilled.match(action)) {
      handleDataUpdate("file", file);
    }
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />
      <InputAndSelect
        amountValue={userData?.amount}
        width="100%"
        placeholder="Amount"
        options={[{ label: "CNY", value: "CNY" }]}
        selectValue={capitalizeCurrencyCode(currencyCode)}
        onAmountChange={handleAmountChange}
      />
      {Boolean(userData?.amount && userData?.amount < MIN_CNY_AMOUNT) && (
        <Error>Minimum payout amount is {MIN_CNY_AMOUNT} CNY</Error>
      )}
      <CustomSelect
        $width="100%"
        placeholder="Payment method"
        options={cnyTypeOfpaymentMethod.map((option) => ({
          label: option.name,
          value: option.value,
        }))}
        onChange={(data) => handleDataUpdate("paymentMethod", data)}
        value={userData?.paymentMethod}
        lightBorder
      />
      <section>
        <RateBox>
          <span className="fee-box">
            x
            <span>{formatCurrencyAmount("", userData?.rate ?? 0, 2) ?? 0}</span>
          </span>
          <span>Exchange rate</span>
        </RateBox>
      </section>

      <section>
        <RateBox>
          <span className="fee-box">
            +<span>{formatCurrencyAmount("", userData?.fee ?? 0, 2) ?? 0}</span>
          </span>
          <span>Fees</span>
        </RateBox>
      </section>

        <CustomSelect
            $width="100%"
            placeholder="Purpose"
            options={cnyPaymentPurpose
                .filter((option) => {
                    const paymentFilterMap = {
                        "bank_account_business": "Goods purchased",
                        "wechatpay": "Family support",
                        "alipay": "Family support",
                    };
                    return paymentMethod in paymentFilterMap
                        ? option.name === paymentFilterMap[paymentMethod]
                        : true;
                })
                .map((option) => ({
                    label: option.name,
                    value: option.value,
                }))}
            onChange={(data) => handleDataUpdate("paymentPurpose", data)}
            value={userData?.paymentPurpose}
            lightBorder
        />



       <div>
           <InputAndSelect
               amountValue={userData?.totalAmount?.toFixed(2) ?? ""}
               width="100%"
               placeholder="Total you will be charged"
               options={walletToDebit}
               selectValue={"USD"}
           />
           {userData?.balanceError && (
               <BalanceExceptionWrapper>
                  <NotifyIcon /> <span>Exceeds your wallet balance.</span>
                   <Link to="/wallets/usd"> Top-up funds</Link>
               </BalanceExceptionWrapper>
           )}
       </div>



      <InputWrapper>
        <h1>Invoice (optional)</h1>
        <p>
          This is required for CNY-B2B (Chinese Yuan Business-to-Business)
          transactions.
        </p>
        <DocumentUpload
          showExtra
          onFileChange={(file: File | null) =>
            handleFileChange("Invoice", file)
          }
        />
      </InputWrapper>

      <Button
        label="Continue"
        onClick={handleNextStep}
        height="48px"
        disabled={
          loading ||
          !userData?.amount ||
          userData?.amount < MIN_CNY_AMOUNT ||
          !userData?.rate ||
          !userData?.paymentMethod ||
          !userData?.paymentPurpose || userData?.balanceError
        }
      />
    </ContentWrapper>
  );
};

export default Amount;
